import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { CoreUser } from "./types/entities";
import { ExpertByIdQueryParameters } from "./types/queries";
import { User } from "./user";
import { constructUser, getExpert, postLogin, postLogout } from "./userAPI";
import { clearPostLoginRedirect } from "../login/postLoginRedirect";

export interface CurrentUserState {
  user: User | null;
  status: string;
}

const initialState: CurrentUserState = {
  user: null,
  status: "",
};

export const fetchExpertById = createAsyncThunk<
  CoreUser,
  ExpertByIdQueryParameters,
  { state: RootState }
>(
  "currentUser/fetchExpertById",
  async (
    filters: ExpertByIdQueryParameters,
    { requestId },
  ): Promise<CoreUser> => getExpert(filters, { requestId }),
);

export const signIn = createAsyncThunk(
  "currentUser/signIn",
  async (user: { username: string; password: string }) => {
    await postLogin(user.username, user.password);
    if (process.env.REACT_APP_USE_LEGACY_LOGIN) {
      return constructUser();
    }
    return await constructUser();
  },
);

export const loadUserSession = createAsyncThunk(
  "currentUser/loadUserSession",
  async () => {
    return constructUser();
  },
);

export const signOut = createAsyncThunk("currentUser/signOut", async () => {
  return await postLogout();
});

export const userSlice = createSlice({
  name: "currentUser",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.status = "succeeded";
    },
    unsetUser: (state) => {
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.status = "pending";
      })
      .addCase(
        signIn.fulfilled,
        (state, action: PayloadAction<User | null>) => {
          state.user = action.payload;
          state.status = "succeeded";
          localStorage.setItem("isLoggedIn", "true");
        },
      )
      .addCase(signIn.rejected, (state) => {
        state.status = "rejected";
      })
      .addCase(signOut.rejected, (state) => {
        state.status = "rejected";
      })
      .addCase(signOut.fulfilled, (state) => {
        state.status = "signedOut";
        state.user = null;
        localStorage.setItem("isLoggedIn", "false");
        clearPostLoginRedirect();
      })
      .addCase(loadUserSession.pending, (state) => {
        state.status = "pending";
      })
      .addCase(loadUserSession.rejected, (state) => {
        if (state.user) {
          state.status = "succeeded";
          return;
        }
        state.status = "rejected";
        state.user = null;
      })
      .addCase(
        loadUserSession.fulfilled,
        (state, action: PayloadAction<User | null>) => {
          state.status = "succeeded";
          if (action.payload) {
            state.user = action.payload;
            localStorage.setItem("isLoggedIn", "true");
            return;
          }
          if (!state.user) {
            state.user = null;
          }
        },
      );
  },
});

export const { setUser, unsetUser } = userSlice.actions;

export default userSlice.reducer;
